import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { ActionType, ObjectTypes } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FiLikesService } from '@fi-app/shared/fi-likes/services/fi-likes.service';
import { IReactionModel } from '@fi-shared/fi-likes/fi-likes';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import {
    FiReadsUserType,
    FiUserInteractionsDialogComponent,
} from '@fi-shared/fi-news-feed-shared/fi-news-feed-buttons/fi-reads-count-button/fi-reads-user-list-data-dialog/fi-reads-user-list-data-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { IReactionsUpdatedEventArgs } from '../fi-signal-r/fi-signal-r';
import { isDefined } from '../utils';
import { Animations } from '../utils/animations';
import { FiInfoIconMode } from '../info-icon/info-icon.component';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { FiLikesFactoryService } from '@fi-app/shared/fi-likes/services/fi-likes-factory.service';

@Component({
    selector: 'fi-likes',
    templateUrl: './fi-likes.component.html',
    styleUrls: ['./fi-likes.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-likes' },
    standalone: false,
    animations: [Animations.fadeWidth()],
})
export class FiLikesComponent implements OnInit {
    @Input() objectId: number;
    @Input() objectType: ObjectTypes;
    @Input() likesData: IReactionModel[] = [];

    @Input() isDisabled: boolean = false;

    @HostBinding('class.icon-template')
    @Input()
    isIconTemplate: boolean = false;

    protected readonly likesToShowCount: number = 10;
    protected isShowInfo: boolean = false;
    protected readonly isDefined = isDefined;
    protected readonly FiInfoIconMode = FiInfoIconMode;

    private _isLikeInProcess$ = new BehaviorSubject<boolean>(false);
    public get toggleLikeDisabled$(): Observable<boolean> {
        return this._isLikeInProcess$.asObservable();
    }

    private appTextService = inject(AppTextService);
    private userDataService = inject(FiUserDataService);
    private likesService = inject(FiLikesService);
    private cdr = inject(ChangeDetectorRef);
    private fiSignalRService = inject(FiSignalRService);
    private fiDialogsService = inject(FiDialogsService);
    private sharedActionsBaseService = inject(SharedActionsBaseService);
    private fiLikesFactoryService = inject(FiLikesFactoryService);

    public ngOnInit(): void {
        this.subscribeToReactionsUpdatedEvent();
    }

    private subscribeToReactionsUpdatedEvent(): void {
        this.fiSignalRService.reactionsUpdatedEvent.subscribe((data) => {
            if (this.isCurrentLikeUpdated(data)) {
                this.updateReactionsData(data);
            }
        }, this.ngOnDestroy$);
    }

    private isCurrentLikeUpdated(data: IReactionsUpdatedEventArgs): boolean {
        return (
            data.ObjectId === this.objectId &&
            data.ObjectType === this.objectType &&
            data.ClientGuid !== getClientGuid()
        );
    }

    private updateReactionsData(data: IReactionsUpdatedEventArgs): void {
        data.Reactions.forEach((like) => {
            const currentLikeIdx = this.likesData.findIndex((x) => x.UserId === like.UserId);
            if (data.ActionType === ActionType.Create && currentLikeIdx === -1) {
                this.likesData.push(like);
            } else if (data.ActionType === ActionType.Delete && currentLikeIdx !== -1) {
                this.likesData.splice(currentLikeIdx, 1);
            }
            this.cdr.detectChanges();
        });
    }

    public toggleInfoShownStatus(newValue: boolean): void {
        this.isShowInfo = newValue;
    }

    protected openUserInteractionsDialog(): void {
        this.fiDialogsService.custom({
            hideFooter: true,
            hideHeader: true,
            contentComponentRef: FiUserInteractionsDialogComponent,
            contentComponentData: {
                objectId: this.objectId,
                objectType: this.objectType,
                readsUserType: FiReadsUserType.LikedBy,
                usersData: this.likesData,
            },
            config: {
                panelClass: 'fi-reads-user-list-data-panel',
            },
        });
    }

    protected getLikesInfo(): string {
        const likesCount = this.likesData.length;
        return likesCount + ' ' + this.appTextService.get(likesCount === 1 ? 'common.like_noun' : 'common.likes');
    }

    public toggleLike($event: MouseEvent): void {
        $event.preventDefault();
        if (this._isLikeInProcess$.value) {
            return;
        }

        this._isLikeInProcess$.next(true);
        const currentUserLike = this.getCurrentUsersLike();
        if (currentUserLike) {
            this.deleteLike(currentUserLike);
        } else {
            this.saveLike();
        }
    }

    private saveLike(): Promise<void> {
        const newLike = this.fiLikesFactoryService.getNewCommonLikeInstance(this.objectId, this.objectType);
        return this.likesService
            .saveLike(this.fiLikesFactoryService.getLikeInstanceForSave(newLike))
            .then(() => {
                this.likesData.push(newLike);
                this.cdr.markForCheck();
            })
            .finally(() => this._isLikeInProcess$.next(false));
    }

    private deleteLike(currentUserLike: IReactionModel): Promise<void> {
        return this.likesService
            .deleteLike(this.fiLikesFactoryService.getLikeInstanceForSave(currentUserLike))
            .then(() => {
                this.likesData.splice(this.likesData.indexOf(currentUserLike), 1);
                this.cdr.markForCheck();
            })
            .finally(() => this._isLikeInProcess$.next(false));
    }

    public getCurrentUsersLike(): IReactionModel | undefined {
        return (
            this.userDataService.userData &&
            this.likesData.find((like) => like.UserId === this.userDataService.getUserId())
        );
    }

    public getLikeActionText(): string {
        const currentUserLike = this.getCurrentUsersLike();
        return this.appTextService.get(currentUserLike ? 'common.liked' : 'common.like');
    }

    public getLikesRemainText(): string | null {
        if (this.likesData?.length > this.likesToShowCount)
            return String.format(this.appTextService.get('and_others'), this.likesData.length - this.likesToShowCount);
        return null;
    }

    protected openUserViewPopup(userId: number): void {
        this.sharedActionsBaseService.openUserViewPopup(userId);
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
